import type { Product } from '@/types';
import cn from 'classnames';
import Button from '@/components/ui/button-old';
import Card from '@/components/product/card';
import CardGenerator from '@/components/models/card';
import ProductCardLoader from '@/components/product/product-loader';
import rangeMap from '@/lib/range-map';
import { useTranslation } from 'next-i18next';
import { useEffect, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import Link from 'next/link';
import { useRouter } from 'next/router';
import routes from '@/config/routes';

interface GridProps {
  products: Product[];
  onLoadMore?: () => void;
  hasNextPage?: boolean;
  isLoadingMore?: boolean;
  isLoading?: boolean;
  limit?: number;
  disableAutoLoad?: boolean;
}

export default function Grid({
  products,
  onLoadMore,
  hasNextPage,
  isLoadingMore,
  isLoading,
  limit = 15,
  disableAutoLoad,
}: GridProps) {
  const isGridCompact = true;
  const { t } = useTranslation('common');
  const [scrollCount, setScrollCount] = useState(0);
  const shouldDisableAutoScroll = scrollCount >= 4;
  const { ref, inView } = useInView();
  const router = useRouter();

  const isMarketplace = router.pathname.includes(routes.marketplace);

  useEffect(() => {
    if (inView && !shouldDisableAutoScroll && !disableAutoLoad) {
      if (onLoadMore) {
        onLoadMore();
        setScrollCount((prevCount) => prevCount + 1);
      }
    }
  }, [inView]);

  return (
    <div
      id="searchResults"
      className="w-full px-0 pt-0 pb-16 md:px-6 md:pb-10 md:pt-6 lg:px-7 lg:pb-12 3xl:px-8"
    >
      <div
        className="grid auto-rows-auto grid-cols-2 gap-3 md:gap-4 lg:gap-5 xl:gap-6"
        style={{
          gridTemplateColumns: `repeat(auto-fit, minmax(min(calc(50% - 6px), 212px), 1fr))`,
        }}
      >
        {isLoading
          ? rangeMap(12, (i) => (
              <ProductCardLoader key={i} uniqueKey={`product-${i}`} />
            ))
          : products.map((product) => {
              const isVideoProduct =
                (product?.type?.slug === 'video' ||
                  product?.type_slug === 'video') &&
                product?.gallery?.length > 0;
                return product.is_custom_ai_model === true ||
                product.is_custom_ai_model === 1 ||
                product.is_custom_ai_model === 'true' ? (
                <CardGenerator key={product.id} product={product} />
              ) : (
                <div
                  key={product.id}
                  className={cn(
                    'min-w-0',
                    isVideoProduct && 'sm:col-span-1 md:col-span-1'
                  )}
                >
                  <Card product={product} />
                </div>
              );
            })}
      </div>

      {!isLoading &&
        !isLoadingMore &&
        products &&
        !hasNextPage &&
        isMarketplace && (
          <div className="mt-8 overflow-hidden rounded-xl bg-gradient-to-br from-light-300 to-light-200 p-6 text-center shadow-sm duration-300 transition-shadow hover:shadow-md dark:from-dark-250 dark:to-dark-300">
            <div className="relative">
              <svg
                className="absolute -left-2 -top-2 h-24 w-24 rotate-[-15deg] text-brand/10 dark:text-brand/20"
                viewBox="0 0 24 24"
                fill="currentColor"
              >
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1.41 16.09V20h-2.67v-1.93c-1.71-.36-3.16-1.46-3.27-3.4h1.96c.1 1.05.82 1.87 2.65 1.87 1.96 0 2.4-.98 2.4-1.59 0-.83-.44-1.61-2.67-2.14-2.48-.6-4.18-1.62-4.18-3.67 0-1.72 1.39-2.84 3.11-3.21V4h2.67v1.95c1.86.45 2.79 1.86 2.85 3.39H14.3c-.05-1.11-.64-1.87-2.22-1.87-1.5 0-2.4.68-2.4 1.64 0 .84.65 1.39 2.67 1.91s4.18 1.39 4.18 3.91c-.01 1.83-1.38 2.83-3.12 3.16z" />
              </svg>

              <div className="mb-6">
                <span className="inline-block rounded-full bg-brand/10 px-3 py-1 text-xs font-medium text-brand dark:bg-brand/20">
                  Creator Opportunity
                </span>
              </div>
              <h3 className="mb-2 text-2xl font-bold text-dark dark:text-light">
                Be Part of Something Big!
              </h3>
              <p className="mx-auto mb-8 max-w-md text-base text-gray-500 dark:text-gray-400">
                Join our creative community and turn your digital assets into
                income. The marketplace is growing, and we need creators like
                you!
              </p>
              <Link
                href="/become-seller"
                className="group relative inline-flex animate-wiggle items-center justify-center overflow-hidden rounded-lg bg-brand p-0.5 font-medium text-light duration-300 transition-all hover:animate-none hover:bg-gradient-to-r hover:from-brand hover:to-purple-600 dark:text-light"
              >
                <span className="relative flex items-center gap-2 rounded-md px-6 py-3 duration-300 transition-all group-hover:bg-opacity-0">
                  <span>Start Selling Today</span>
                  <svg
                    className="h-4 w-4 duration-300 transition-transform group-hover:translate-x-1"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M13 7l5 5m0 0l-5 5m5-5H6"
                    />
                  </svg>
                </span>
              </Link>
            </div>
          </div>
        )}

      {hasNextPage && (
        <div className="mt-8 grid place-content-center md:mt-10">
          <Button
            ref={ref}
            onClick={onLoadMore}
            disabled={isLoadingMore}
            isLoading={isLoadingMore}
          >
            {t('text-loadmore')}
          </Button>
        </div>
      )}
    </div>
  );
}
